// Industries.jsx
import { Helmet } from 'react-helmet-async';
import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import {
  Flame, Anchor, Building, Cog, Zap, Truck,
  ShieldCheck, ArrowRight, ChevronRight, Lock,
  Users, Briefcase, Building2
} from 'lucide-react';
import './Industries.css';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';

const site = 'https://qualion.technology';

const iconMap = { Flame, Anchor, Building, Cog, Zap, Truck };

const industriesData = {
  // ── I-01 — Hero: concise, no universal-coverage claim ──
  hero: {
    eyebrow: 'INDUSTRY APPLICATIONS',
    headline: 'Built for industries where competence must be proven.',
    subheadline:
      'Public industry pages show application context only. Standards mapping, resource allocation and detailed scopes are reviewed inside authenticated project and competency context.',
  },

  // ── I-02 + I-03 + I-05 — Six approved sectors, compact content only ──
  industries: [
    {
      id: 'oil-gas',
      title: 'Oil & Gas',
      icon: 'Flame',
      tagline: 'Upstream to downstream — verified capability at every stage.',
      typical_requirement:
        'Asset owners and contractors need inspection professionals whose competence and certifications can be verified before deployment.',
      professional_categories:
        'Inspectors · Surveyors · QA/QC engineers · Project personnel',
      general_outcome:
        'Verified professionals deployed against structured project requirements.',
    },
    {
      id: 'maritime-offshore',
      title: 'Maritime & Offshore',
      icon: 'Anchor',
      tagline: 'Hull, structure and equipment — traceable assurance.',
      typical_requirement:
        'Marine and offshore operations require surveyors with verifiable experience across classification and structural inspection activities.',
      professional_categories:
        'Surveyors · Inspectors · Marine engineers · Project personnel',
      general_outcome:
        'Consistent, auditable evidence of professional capability across deployments.',
    },
    {
      id: 'infrastructure',
      title: 'Infrastructure',
      icon: 'Building',
      tagline: 'Civil and structural assurance that protects the public.',
      typical_requirement:
        'Infrastructure owners and delivery teams need inspection professionals whose competence is evidenced and auditable across long project lifecycles.',
      professional_categories:
        'Inspectors · Engineers · Quality specialists · Project personnel',
      general_outcome:
        'Structured competency and inspection records aligned to programme assurance needs.',
    },
    {
      id: 'manufacturing',
      title: 'Manufacturing',
      icon: 'Cog',
      tagline: 'In-process and final inspection — quality built in.',
      typical_requirement:
        'Manufacturers and QA providers need quality personnel whose qualifications and evidence are verified before sign-off authority is granted.',
      professional_categories:
        'QA/QC specialists · Inspectors · Engineers · Project personnel',
      general_outcome:
        'Controlled access to verified quality capability, with auditable records.',
    },
    {
      id: 'energy-renewables',
      title: 'Energy & Renewables',
      icon: 'Zap',
      tagline: 'Conventional and renewable assets — assurance for the transition.',
      typical_requirement:
        'Energy operators require inspection professionals with demonstrated competence across electrical, mechanical and structural systems.',
      professional_categories:
        'Inspectors · Engineers · Quality specialists · Project personnel',
      general_outcome:
        'Verified capability deployed under controlled, consent-based access.',
    },
    {
      id: 'logistics-yards',
      title: 'Logistics & Yard Operations',
      icon: 'Truck',
      tagline: 'Equipment, cargo and facilities — inspection at scale.',
      typical_requirement:
        'Logistics and yard operators need periodic, documented inspection of equipment, cargo and facilities across distributed sites.',
      professional_categories:
        'Inspectors · Surveyors · Quality specialists · Project personnel',
      general_outcome:
        'Consistent inspection records and evidence across multiple locations.',
    },
  ],

  // ── I-02 + I-08 — Cross-industry capability, not an industry ──
  crossIndustry: {
    eyebrow: 'CROSS-INDUSTRY CAPABILITY',
    title: 'Quality, Inspection & Compliance',
    body:
      'Quality, inspection and compliance are cross-industry capabilities supported by the platform, not a market sector. They apply wherever verified technical capability and auditable project records matter.',
  },

  // ── I-08 — Controlled CTA ──
  cta: {
    headline: 'Discuss your organisation’s requirements through controlled access.',
    subheadline:
      'Every organisation path enters the same controlled identity and verification service. Approval is not automatic and capability is not implied.',
    primary: 'Join Ecosystem',
    secondary: 'Sign In',
  },
};

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.55, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function IndustriesPage() {
  // ── I-06 — Navigation state (active section for scroll highlight) ──
  const [activeAnchor, setActiveAnchor] = useState(industriesData.industries[0].id);

  const scrollTo = (id) => {
    setActiveAnchor(id);
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleJoin = () => {
    window.location.href = '/join-ecosystem';
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${site}/industries#webpage`,
    name: 'Industry Applications | Qualion',
    url: `${site}/industries`,
    isPartOf: { '@id': `${site}/#website` },
    about: { '@id': `${site}/#organization` },
  };

  return (
    <>
      <Helmet>
        <title>Industry Applications | Qualion</title>
        <meta
          name="description"
          content="Public industry pages show application context only. Standards mapping, resource allocation and detailed scopes are reviewed inside authenticated project and competency context."
        />
        <link rel="canonical" href={`${site}/industries`} />
        <meta property="og:title" content="Industry Applications | Qualion" />
        <meta
          property="og:description"
          content="Application context across six assured industry sectors."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${site}/industries`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Navbar />

      <main className="industries-page">
        {/* ── HERO (I-01) ── */}
        <section className="industries-hero">
          <div className="industries-hero-grid-overlay" />
          <div className="industries-hero-accent-line" />
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              className="industries-hero-content"
            >
              <div className="industries-hero-eyebrow">
                <div className="industries-hero-line" />
                <span>{industriesData.hero.eyebrow}</span>
              </div>
              <h1 className="industries-hero-title">{industriesData.hero.headline}</h1>
              <p className="industries-hero-subtitle">{industriesData.hero.subheadline}</p>
            </motion.div>
          </div>
        </section>

        {/* ── INDUSTRY SELECTOR CARDS (I-06) — one compact card per sector ── */}
        <section className="industries-selector-section">
          <div className="container">
            <FadeUp className="industries-selector-header">
              <h2>Six sectors. Application context only.</h2>
              <p>Select a sector to jump to its summary. Detailed scopes are reviewed inside approved access.</p>
            </FadeUp>

            <div className="industries-selector-grid">
              {industriesData.industries.map((ind, i) => {
                const Icon = iconMap[ind.icon] || Cog;
                return (
                  <FadeUp key={ind.id} delay={i * 0.04}>
                    <button
                      type="button"
                      onClick={() => scrollTo(ind.id)}
                      className={`industries-selector-card ${
                        activeAnchor === ind.id ? 'active' : ''
                      }`}
                    >
                      <div className="industries-selector-icon">
                        <Icon size={18} />
                      </div>
                      <div className="industries-selector-body">
                        <span className="industries-selector-title">{ind.title}</span>
                        <span className="industries-selector-tagline">{ind.tagline}</span>
                      </div>
                      <ChevronRight size={14} className="industries-selector-arrow" />
                    </button>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── PER-INDUSTRY SUMMARY SECTIONS (I-03, I-04, I-05) ── */}
        <section className="industries-detail-section">
          <div className="container">
            {industriesData.industries.map((ind, index) => {
              const Icon = iconMap[ind.icon] || Cog;
              const isAlt = index % 2 === 1;
              return (
                <FadeUp key={ind.id} delay={0.05}>
                  <article
                    id={ind.id}
                    className={`industries-block ${isAlt ? 'industries-block-alt' : ''}`}
                  >
                    <div className="industries-block-grid">
                      <div className="industries-block-content">
                        <div className="industries-block-header">
                          <div className="industries-block-icon-wrapper">
                            <Icon size={22} className="industries-block-icon" />
                          </div>
                          <h3 className="industries-block-title">{ind.title}</h3>
                        </div>
                        <p className="industries-block-tagline">{ind.tagline}</p>

                        <div className="industries-block-fields">
                          <div className="industries-block-field">
                            <span className="industries-block-field-label">
                              Typical requirement
                            </span>
                            <p className="industries-block-field-body">
                              {ind.typical_requirement}
                            </p>
                          </div>

                          <div className="industries-block-field">
                            <span className="industries-block-field-label">
                              Professional categories
                            </span>
                            <p className="industries-block-field-body">
                              {ind.professional_categories}
                            </p>
                          </div>

                          <div className="industries-block-field">
                            <span className="industries-block-field-label">
                              General outcome
                            </span>
                            <p className="industries-block-field-body">
                              {ind.general_outcome}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Neutral standards note — no public standards list (I-04) */}
                      <aside className="industries-block-aside">
                        <div className="industries-block-aside-card">
                          <div className="industries-block-aside-icon">
                            <Lock size={16} />
                          </div>
                          <div className="industries-block-aside-title">
                            Standards & scopes
                          </div>
                          <p className="industries-block-aside-body">
                            Applicable standards, scopes and equivalence are
                            reviewed inside authenticated project and
                            competency context. No public standard mapping is
                            displayed.
                          </p>
                        </div>
                      </aside>
                    </div>
                  </article>
                </FadeUp>
              );
            })}
          </div>
        </section>

        {/* ── CROSS-INDUSTRY CAPABILITY (I-02) ── */}
        <section className="industries-cross-section">
          <div className="container">
            <FadeUp className="industries-cross-content">
              <div className="industries-cross-eyebrow">
                <div className="industries-cross-line" />
                <span>{industriesData.crossIndustry.eyebrow}</span>
                <div className="industries-cross-line" />
              </div>
              <h2>{industriesData.crossIndustry.title}</h2>
              <p>{industriesData.crossIndustry.body}</p>

              <div className="industries-cross-chips">
                <span className="industries-cross-chip">
                  <ShieldCheck size={14} /> Quality
                </span>
                <span className="industries-cross-chip">
                  <ShieldCheck size={14} /> Inspection
                </span>
                <span className="industries-cross-chip">
                  <ShieldCheck size={14} /> Compliance
                </span>
              </div>
            </FadeUp>
          </div>
        </section>

        {/* ── OVERVIEW GRID (I-07) — no fixed count in headline ── */}
        <section className="industries-grid-section">
          <div className="industries-grid-bg" />
          <div className="container">
            <FadeUp className="industries-grid-header">
              <h2>Multi-industry relevance. One controlled ecosystem.</h2>
              <p>Application context for each sector above. Detailed scopes and standards live inside approved access.</p>
            </FadeUp>
            <div className="industries-grid">
              {industriesData.industries.map((ind, i) => {
                const Icon = iconMap[ind.icon] || Cog;
                return (
                  <FadeUp key={ind.id} delay={i * 0.05}>
                    <button
                      type="button"
                      onClick={() => scrollTo(ind.id)}
                      className={`industries-grid-item ${
                        activeAnchor === ind.id ? 'active' : ''
                      }`}
                    >
                      <div className="industries-grid-icon-wrapper">
                        <Icon size={18} className="industries-grid-icon" />
                      </div>
                      <h3 className="industries-grid-title">{ind.title}</h3>
                      <p className="industries-grid-tagline">{ind.tagline}</p>
                    </button>
                  </FadeUp>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── CTA (I-08) ── */}
        <section className="industries-cta-section">
          <div className="container">
            <FadeUp>
              <div className="industries-cta-content">
                <div className="industries-cta-eyebrow">
                  <div className="industries-cta-line" />
                  <span>CONTROLLED ACCESS</span>
                  <div className="industries-cta-line" />
                </div>
                <h2 className="industries-cta-headline">
                  {industriesData.cta.headline}
                </h2>
                <p className="industries-cta-subheadline">
                  {industriesData.cta.subheadline}
                </p>
                <div className="industries-cta-buttons">
                  <button
                    type="button"
                    onClick={handleJoin}
                    className="industries-cta-primary"
                  >
                    {industriesData.cta.primary} <ArrowRight size={16} />
                  </button>
                  <a href="/login" className="industries-cta-secondary">
                    {industriesData.cta.secondary}
                  </a>
                </div>
              </div>
            </FadeUp>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}